import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../services/api';

// Use relative URL so it works on any domain (HTTPS → WSS automatically)
const WS_URL = window.location.origin.replace('https://', 'wss://').replace('http://', 'ws://');
const RD_PATH = '/remote-ws';

export function RemoteDesktopPage() {
  const { userId } = useParams<{ userId: string }>();
  const [status, setStatus] = useState<'disconnected' | 'connecting' | 'connected' | 'agent_offline'>('disconnected');
  const [fps, setFps] = useState(0);
  const [resolution, setResolution] = useState({ w: 0, h: 0 });
  const [controlEnabled, setControlEnabled] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [onlineAgents, setOnlineAgents] = useState<number[]>([]);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const frameCountRef = useRef(0);
  const fpsIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.get('/users').then(r => setUsers(r.data));
    api.get('/remote-desktop/online-agents').then(r => setOnlineAgents(r.data.agents || []));
    const interval = setInterval(() => {
      api.get('/remote-desktop/online-agents').then(r => setOnlineAgents(r.data.agents || []));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const connect = useCallback(async (targetUserId: string) => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    setStatus('connecting');
    const token = localStorage.getItem('at_token');
    const ws = new WebSocket(`${WS_URL}${RD_PATH}?type=viewer&token=${token}&targetUserId=${targetUserId}`);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[RemoteDesktop] Viewer connected');
    };

    ws.onmessage = (event) => {
      if (typeof event.data === 'string') {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'agent_connected') {
            setStatus('connected');
            startFpsCounter();
          } else if (msg.type === 'agent_offline') {
            setStatus('agent_offline');
          } else if (msg.type === 'agent_disconnected') {
            setStatus('agent_offline');
          }
        } catch {}
        return;
      }

      // Binary data = frame
      // Format: JSON header line + \n + JPEG bytes
      const buffer = new Uint8Array(event.data);
      const newlineIdx = buffer.indexOf(10); // '\n'
      if (newlineIdx === -1) return;

      try {
        const headerStr = new TextDecoder().decode(buffer.slice(0, newlineIdx));
        const header = JSON.parse(headerStr);
        const jpegData = buffer.slice(newlineIdx + 1);

        setResolution({ w: header.width, h: header.height });
        frameCountRef.current++;

        // Draw frame on canvas
        const canvas = canvasRef.current;
        if (!canvas) return;

        const blob = new Blob([jpegData], { type: 'image/jpeg' });
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          const ctx = canvas.getContext('2d');
          if (!ctx) return;
          canvas.width = header.width;
          canvas.height = header.height;
          ctx.drawImage(img, 0, 0);
          URL.revokeObjectURL(url);
        };
        img.src = url;
      } catch {}
    };

    ws.onclose = () => {
      setStatus('disconnected');
      stopFpsCounter();
    };

    ws.onerror = () => {
      setStatus('disconnected');
    };
  }, []);

  const disconnect = () => {
    wsRef.current?.close();
    wsRef.current = null;
    setStatus('disconnected');
    stopFpsCounter();
  };

  const startFpsCounter = () => {
    frameCountRef.current = 0;
    fpsIntervalRef.current = setInterval(() => {
      setFps(frameCountRef.current);
      frameCountRef.current = 0;
    }, 1000);
  };

  const stopFpsCounter = () => {
    if (fpsIntervalRef.current) clearInterval(fpsIntervalRef.current);
    setFps(0);
  };

  useEffect(() => {
    if (userId) connect(userId);
    return () => { disconnect(); };
  }, [userId]);

  // Mouse event handlers
  const sendControlEvent = (event: any) => {
    if (!controlEnabled || !wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify(event));
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    sendControlEvent({
      type: 'mousemove',
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      viewWidth: rect.width,
      viewHeight: rect.height,
    });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    sendControlEvent({ type: 'mousedown', button: e.button === 2 ? 'right' : 'left' });
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    sendControlEvent({ type: 'mouseup', button: e.button === 2 ? 'right' : 'left' });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.preventDefault();
    sendControlEvent({ type: 'keydown', key: e.key.toLowerCase() });
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
    sendControlEvent({ type: 'keyup', key: e.key.toLowerCase() });
  };

  const targetUser = users.find(u => u.id === parseInt(userId || '0'));

  const STATUS_COLORS: Record<string, string> = {
    disconnected: 'var(--text3)',
    connecting: 'var(--accent)',
    connected: 'var(--green)',
    agent_offline: 'var(--red)',
  };

  const STATUS_LABELS: Record<string, string> = {
    disconnected: 'Desconectado',
    connecting: 'Conectando...',
    connected: `Conectado · ${fps} FPS · ${resolution.w}×${resolution.h}`,
    agent_offline: 'Agente no disponible',
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#000' }}>
      {/* Toolbar */}
      <div style={{
        height: 44,
        background: 'var(--bg2)',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '0 16px',
        flexShrink: 0,
      }}>
        <Link to="/employees" style={{ color: 'var(--text3)', fontSize: 12, textDecoration: 'none' }}>← Empleados</Link>
        <span style={{ color: 'var(--border)' }}>|</span>
        
        {/* User selector */}
        <select
          className="input"
          style={{ width: 180, padding: '4px 8px', fontSize: 12 }}
          value={userId || ''}
          onChange={e => { if (e.target.value) window.location.href = `/remote/${e.target.value}`; }}
        >
          <option value="">Seleccionar empleado</option>
          {users.map(u => (
            <option key={u.id} value={u.id}>
              {u.name} {onlineAgents.includes(u.id) ? '🟢' : '⚫'}
            </option>
          ))}
        </select>

        {/* Status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLORS[status] }} />
          <span style={{ fontSize: 12, color: 'var(--text2)' }}>{STATUS_LABELS[status]}</span>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {/* Control toggle */}
          {status === 'connected' && (
            <button
              onClick={() => setControlEnabled(!controlEnabled)}
              className={`btn ${controlEnabled ? 'btn-danger' : 'btn-secondary'}`}
              style={{ fontSize: 11, padding: '4px 10px' }}
            >
              {controlEnabled ? '🖱️ Control activo' : '👁️ Solo vista'}
            </button>
          )}

          {/* Connect/Disconnect */}
          {status === 'disconnected' || status === 'agent_offline' ? (
            <button
              onClick={() => userId && connect(userId)}
              className="btn btn-primary"
              style={{ fontSize: 11, padding: '4px 10px' }}
              disabled={!userId}
            >
              Conectar
            </button>
          ) : (
            <button
              onClick={disconnect}
              className="btn btn-danger"
              style={{ fontSize: 11, padding: '4px 10px' }}
            >
              Desconectar
            </button>
          )}
        </div>
      </div>

      {/* Canvas area */}
      <div ref={containerRef} style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#111' }}>
        {status === 'connected' ? (
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              cursor: controlEnabled ? 'crosshair' : 'default',
              display: 'block',
            }}
            onMouseMove={handleMouseMove}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            tabIndex={0}
            onContextMenu={e => e.preventDefault()}
          />
        ) : (
          <div style={{ textAlign: 'center', color: 'var(--text3)' }}>
            {status === 'connecting' && (
              <div>
                <div className="spinner-large" style={{ margin: '0 auto 16px' }} />
                <div style={{ fontSize: 14 }}>Conectando al escritorio de {targetUser?.name || 'empleado'}...</div>
              </div>
            )}
            {status === 'agent_offline' && (
              <div>
                <div style={{ fontSize: 48, marginBottom: 12 }}>💻</div>
                <div style={{ fontSize: 16, color: 'var(--text2)', marginBottom: 8 }}>Agente no disponible</div>
                <div style={{ fontSize: 12 }}>El agente AccioTrack no está conectado en este equipo</div>
                <div style={{ marginTop: 16, fontSize: 11 }}>
                  Agentes online: {onlineAgents.length > 0
                    ? onlineAgents.map(id => users.find(u => u.id === id)?.name || `#${id}`).join(', ')
                    : 'ninguno'}
                </div>
              </div>
            )}
            {status === 'disconnected' && (
              <div>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🖥️</div>
                <div style={{ fontSize: 16, color: 'var(--text2)', marginBottom: 8 }}>Escritorio remoto AccioTrack</div>
                <div style={{ fontSize: 12 }}>Selecciona un empleado y pulsa Conectar</div>
                <div style={{ marginTop: 12, fontSize: 11, color: 'var(--green)' }}>
                  {onlineAgents.length} agente{onlineAgents.length !== 1 ? 's' : ''} online
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom bar */}
      {status === 'connected' && (
        <div style={{
          height: 28,
          background: 'var(--bg2)',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
          gap: 16,
          fontSize: 10,
          color: 'var(--text3)',
          flexShrink: 0,
        }}>
          <span>👤 {targetUser?.name}</span>
          <span>📐 {resolution.w}×{resolution.h}</span>
          <span>🎞️ {fps} FPS</span>
          <span>🔒 {controlEnabled ? 'Control remoto activo' : 'Modo lectura'}</span>
          <span style={{ marginLeft: 'auto', color: 'var(--accent)' }}>⚡ AccioTrack Remote Desktop</span>
        </div>
      )}
    </div>
  );
}
